import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import type { ExportConfigResponse } from 'lumen/utils/parity/resources/export-config';

export interface ExportConfig {
  extractDate: string;
  fromDateRangeEnd: string;
  fromDateRangeStart: string;
  fromDates: string[];
  los: number;
  persons: number;
  platform: Platform;
}

/**
 * Collapses the from_dates of every OTA into a from date range, and keeps the unique combinations of platform + range.
 * The Parity Analytics API doesn't accept an explicit list of arrival dates, only a range with a start and end date.
 * The gaps in that range have to be filtered out of the response cross-referenced against `fromDates`.
 */
export function getUniqueExportConfigs(
  exportConfigResponse: ExportConfigResponse,
): ExportConfig[] {
  const uniqueExportConfigs = new Map<string, ExportConfig>();

  Object.entries(exportConfigResponse).forEach(([platform, otas]) => {
    Object.values(otas).forEach((ota) => {
      if (!ota.from_dates.length) {
        return;
      }

      const sortedFromDates = [...ota.from_dates].sort(); // No need for dayjs due to ISO format
      const fromDateRangeStart = sortedFromDates.at(0)!;
      const fromDateRangeEnd = sortedFromDates.at(-1)!;

      const id = `${platform};${fromDateRangeStart};${fromDateRangeEnd}`;
      const existingExportConfig = uniqueExportConfigs.get(id);

      // OTAs sharing a range can still have different gaps, so merge their from_dates
      if (existingExportConfig) {
        existingExportConfig.fromDates = [
          ...new Set([...existingExportConfig.fromDates, ...ota.from_dates]),
        ];
        return;
      }

      uniqueExportConfigs.set(id, {
        platform: parseInt(platform, 10) as Platform,
        los: ota.los,
        persons: ota.persons,
        extractDate: ota.extract_date,
        fromDateRangeStart,
        fromDateRangeEnd,
        fromDates: ota.from_dates,
      });
    });
  });

  return [...uniqueExportConfigs.values()];
}
